import { useEffect, useState } from 'react';
import { compatApi } from '../utils/Intercepter';
import { PURPLE, MINT, hexToRgba } from '../utils/designUtils';

/**
 * MASTER 위임 모달 — 조직 내 승인된 재직 구성원 중 후임을 선택해 MASTER 권한을 넘긴다.
 * 위임 성공 시 현재 사용자는 STAFF로 강등되므로, 역할 재계산을 위해 루트로 리로드한다.
 *
 * props:
 *   isOpen, onClose
 *   organizationCode  : 대상 조직
 *   currentUserEmail  : 본인(후보에서 제외)
 */
export default function TransferMasterModal({ isOpen, onClose, organizationCode, currentUserEmail }) {
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen || !organizationCode) return;
        setSelected(null);
        setLoading(true);
        (async () => {
            try {
                const res = await compatApi.post('/user/readEmployeeList', organizationCode);
                if (res.ok) {
                    // 후임 자격: 승인된 재직 구성원, 본인·기존 MASTER 제외
                    const list = (res.data || []).filter(u =>
                        !u.leaveDate && u.approved && u.userRole !== 'MASTER'
                        && u.userEmail !== currentUserEmail
                    );
                    setCandidates(list);
                } else {
                    setCandidates([]);
                }
            } catch {
                setCandidates([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [isOpen, organizationCode, currentUserEmail]);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        if (!selected) return;
        if (!window.confirm(
            `${selected.userName}(${selected.userEmail})님에게 MASTER 권한을 위임하시겠습니까?\n\n위임하면 회원님은 STAFF로 변경되며, 이 작업은 되돌릴 수 없습니다.`
        )) return;
        setSubmitting(true);
        try {
            const res = await compatApi.post('/org/transferMaster', {
                organizationCode,
                newMasterEmail: selected.userEmail,
            });
            if (res.ok) {
                alert('MASTER 권한이 위임되었습니다.');
                window.location.href = '/';
            } else {
                alert(`위임 중 오류가 발생했습니다. (${res.status})`);
                setSubmitting(false);
            }
        } catch (error) {
            const msg = error?.response?.data;
            alert(typeof msg === 'string' && msg ? msg : `위임 중 오류가 발생했습니다: ${error.message}`);
            setSubmitting(false);
        }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 120,
            background: 'rgba(30,25,60,.45)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px',
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: 'white', borderRadius: 20, width: '100%', maxWidth: 480,
                maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column',
                boxShadow: '0 24px 64px -16px rgba(30,25,60,.35)', overflow: 'hidden',
            }}>
                <div style={{ padding: '18px 22px', borderBottom: '1px solid #ececf2' }}>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1f1d2e' }}>MASTER 위임</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#8b8a99' }}>
                        새 MASTER가 될 구성원을 선택하세요. 위임 후 회원님은 STAFF로 변경됩니다.
                    </p>
                </div>

                <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
                    {loading ? (
                        <div style={{ padding: '32px 0', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>불러오는 중…</div>
                    ) : candidates.length === 0 ? (
                        <div style={{ padding: '28px 12px', textAlign: 'center', color: '#8b8a99', fontSize: 13, lineHeight: 1.6 }}>
                            위임할 수 있는 구성원이 없습니다.<br />
                            (승인된 재직 구성원이 있어야 위임할 수 있습니다)
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {candidates.map(u => {
                                const on = selected?.userEmail === u.userEmail;
                                return (
                                    <button key={u.userEmail} onClick={() => setSelected(u)} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                        padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                                        fontFamily: 'inherit', transition: 'all 120ms',
                                        background: on ? hexToRgba(PURPLE, .07) : 'white',
                                        border: on ? `1.5px solid ${PURPLE}` : '1px solid #ececf2',
                                    }}>
                                        <span style={{
                                            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                            color: 'white', fontSize: 12, fontWeight: 700,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        }}>{(u.userName || '?').trim().slice(0, 1)}</span>
                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1f1d2e' }}>{u.userName}</span>
                                            <span style={{ fontSize: 11.5, color: '#8b8a99', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.userEmail}</span>
                                        </div>
                                        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#5d5b6e', background: '#f4f3f9', border: '1px solid #ececf2', padding: '2px 7px', borderRadius: 6 }}>{u.userRole}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={{ padding: '14px 18px', borderTop: '1px solid #ececf2', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button onClick={onClose} style={{
                        height: 36, padding: '0 16px', borderRadius: 9, border: '1px solid #ececf2',
                        background: 'white', color: '#5d5b6e', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>취소</button>
                    <button onClick={handleConfirm} disabled={!selected || submitting} style={{
                        height: 36, padding: '0 18px', borderRadius: 9, border: 'none',
                        background: (!selected || submitting) ? '#c7c5d2' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                        color: 'white', fontSize: 13, fontWeight: 600,
                        cursor: (!selected || submitting) ? 'default' : 'pointer', fontFamily: 'inherit',
                    }}>{submitting ? '처리 중…' : '위임하기'}</button>
                </div>
            </div>
        </div>
    );
}
